import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useApolloClient } from "@apollo/client";

import { UserRole } from "@utils/rbac";
import { GET_ME } from "@/graphql/queries/auth";

type AuthState = {
  userId: string | null;
  role: UserRole | null;
  email: string | null;
  fullName: string | null;
  /** "Darf Aufgabenserien anlegen" (Mitarbeiter tick), see rbac.canCreateSeries. */
  mayCreateSeries: boolean;
};

type AuthContextValue = AuthState & {
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  refreshMe: () => Promise<void>;
  setAuth: (auth: AuthState) => void;
  clearAuth: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const client = useApolloClient();
  const [auth, setAuthState] = useState<AuthState>({
    userId: null,
    role: null,
    email: null,
    fullName: null,
    mayCreateSeries: false,
  });
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const setAuth = useCallback((nextAuth: AuthState) => {
    setAuthState(nextAuth);
  }, []);

  const clearAuth = useCallback(() => {
    setAuthState({
      userId: null,
      role: null,
      email: null,
      fullName: null,
    mayCreateSeries: false,
    });
    client.clearStore().catch(() => {});
  }, [client]);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await client.query({
        query: GET_ME,
        fetchPolicy: "network-only",
      });
      const me = data?.me;
      if (!me?.id) {
        clearAuth();
        return;
      }
      setAuth({
        userId: String(me.id),
        email: me.email ?? null,
        fullName: me.fullName || null,
        role: (me.role as UserRole | null) ?? null,
        mayCreateSeries: Boolean(me.mayCreateSeries),
      });
    } catch {
      clearAuth();
    }
  }, [client, clearAuth, setAuth]);

  useEffect(() => {
    (async () => {
      setIsBootstrapping(true);
      await refreshMe();
      setIsBootstrapping(false);
    })();
  }, [refreshMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...auth,
      isAuthenticated: Boolean(auth.userId),
      isBootstrapping,
      refreshMe,
      setAuth,
      clearAuth,
    }),
    [auth, clearAuth, isBootstrapping, refreshMe, setAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
